import { useQuery } from '@apollo/client';
import {
  IDropdownOption,
  ITextFieldProps,
  Label,
  Pivot,
  PivotItem,
  Stack,
} from '@fluentui/react';
import {
  FormHookAmount,
  FormHookCountryRegion,
  FormHookTextField,
} from 'common/components/FormHooksFields';
import { FormHookCheckBox } from 'common/components/FormHooksFields/FormHookCheckBox';
import { Countries } from 'common/graphql/__generated__/Countries';
import React, { JSX, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { SupplierAlias } from 'settings/account/supplier/view/BasicForm/SupplierAlias';
import { TASupplierDetails_supplier } from '../../__generated__/TASupplierDetails';
import { CompanyAutoComplete } from '../CompanyAutoComplete';
import { SupplierValues } from '../types';
import { useStyles } from './index.styles';
import COUNTRIES from 'common/graphql/Countries.graphql';
enum PivotKeys {
  company = 'Company',
  individual = 'Individual',
}

interface BasicFormProps {
  isNew: boolean;
  isUpdatable: boolean;
  supplier: TASupplierDetails_supplier | null | undefined;
}

export const BasicForm: React.FC<BasicFormProps> = ({
  isNew,
  isUpdatable,
  supplier,
}) => {
  const styles = useStyles();
  const [selectedKey, setSelectedKey] = useState<string>(PivotKeys.company);

  const { setValue } = useFormContext<SupplierValues>();

  const { data: countries } = useQuery<Countries>(COUNTRIES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const onPivotChange = (item: PivotItem | undefined) => {
    setSelectedKey(item?.props.itemKey || PivotKeys.individual);
    if (!!isUpdatable) {
      if (item?.props.itemKey === PivotKeys.individual) {
        setValue('isIndividual', true);
        setValue('isCorporation', false);
      } else {
        setValue('isIndividual', false);
        setValue('isCorporation', true);
      }
    }
  };

  const countryOptions: IDropdownOption[] =
    countries?.countries?.nodes.map((ele) => ({
      key: ele.id,
      text: ele.country,
    })) || [];

  useEffect(() => {
    if (!isNew) {
      if (supplier?.isIndividual) setSelectedKey(PivotKeys.individual);
    } else setSelectedKey(PivotKeys.company);
  }, [isNew, supplier]);

  const { _isPurchaseOrderSettingsUpdatable, _isUpdatable, _isAliasInvoked } = {
    ...supplier,
  };

  //Refer ticket 1396 and 1397 for more details.
  const isCreate = isNew ? true : false;
  const isPurchaseOrderSettingsUpdatable = !!_isPurchaseOrderSettingsUpdatable;
  const isUpdatableAndIsPOUpdatable = !(
    _isUpdatable && isPurchaseOrderSettingsUpdatable
  );

  const disableCheckboxes =
    isCreate ||
    !isPurchaseOrderSettingsUpdatable ||
    isUpdatableAndIsPOUpdatable;

  const disableCompetitiveBids = isCreate || !isPurchaseOrderSettingsUpdatable;
  const onRenderLabel = (props: ITextFieldProps | undefined): JSX.Element => {
    return (
      <Stack horizontal verticalAlign="end" horizontalAlign="space-between">
        <Label disabled={props?.disabled}>{props?.label}</Label>
        {!isCreate && <SupplierAlias supplier={supplier} />}
      </Stack>
    );
  };
  return (
    <>
      <Stack
        tokens={{
          childrenGap: 15,
          padding: '0px 0px 20px 0px',
        }}
      >
        <Pivot
          onLinkClick={onPivotChange}
          selectedKey={selectedKey}
          className={styles.pivot}
        >
          <PivotItem
            itemKey={PivotKeys.company}
            headerText={PivotKeys.company}
          />
          <PivotItem
            itemKey={PivotKeys.individual}
            headerText={PivotKeys.individual}
          />
        </Pivot>
        {selectedKey === PivotKeys.company ? (
          <Stack
            horizontal
            verticalAlign="baseline"
            tokens={{
              childrenGap: 20,
              padding: '0px 20px',
            }}
          >
            <Stack className={styles.inputContainer}>
              <CompanyAutoComplete
                name="companyOrLastName"
                label="Company Name"
                placeholder="Company Name"
                id={'companyNameField'}
                disabled={!isUpdatable || !!_isAliasInvoked}
                required
              />
            </Stack>
          </Stack>
        ) : (
          <>
            <Stack
              horizontal
              verticalAlign="baseline"
              tokens={{
                childrenGap: 20,
                padding: '0px 20px',
              }}
            >
              <Stack className={styles.inputContainer}>
                <FormHookTextField
                  name="firstName"
                  label="First Name"
                  placeholder="Given Name"
                  disabled={!isUpdatable || !!_isAliasInvoked}
                  onRenderLabel={onRenderLabel}
                />
              </Stack>
              <Stack className={styles.inputContainer}>
                <CompanyAutoComplete
                  name="companyOrLastName"
                  label="Last Name"
                  placeholder="Family Name"
                  disabled={!isUpdatable || !!_isAliasInvoked}
                  id={'companyNameField'}
                  required
                />
              </Stack>
            </Stack>
          </>
        )}
        <Label style={{ marginLeft: 20 }}>Address</Label>
        <Stack
          verticalAlign="center"
          tokens={{
            padding: '0px 20px',
          }}
          style={{ marginTop: 0 }}
        >
          <FormHookTextField
            name="supplierAddressesBySupplierId[0].addressLine1"
            label="Line 1)"
            placeholder="Street Number and PO Box"
            disabled={!isUpdatable}
          />
        </Stack>
        <Stack
          horizontal
          verticalAlign="center"
          tokens={{
            childrenGap: 20,
            padding: '0px 20px',
          }}
        >
          <Stack className={styles.inputContainer}>
            <FormHookTextField
              name="supplierAddressesBySupplierId[0].addressLine2"
              label="Line 2)"
              placeholder="Apartment, Suite, Unit, Building, Floor"
              disabled={!isUpdatable}
            />
          </Stack>
          <Stack className={styles.inputContainer}>
            <FormHookTextField
              name="supplierAddressesBySupplierId[0].cityName"
              label="City"
              placeholder="City/Town"
              disabled={!isUpdatable}
            />
          </Stack>
        </Stack>
        <Stack
          horizontal
          verticalAlign="center"
          tokens={{
            childrenGap: 20,
            padding: '0px 20px',
          }}
        >
          <Stack.Item grow={2}>
            <FormHookCountryRegion
              countries={countryOptions}
              countryName={`supplierAddressesBySupplierId[0].countryId`}
              regionName={`supplierAddressesBySupplierId[0].stateRegionId`}
              CountryAutocompleteProps={{
                label: 'Country',
                errorMessage: '',
              }}
              StateRegionAutocompleteProps={{
                label: 'State / Region / Province',
                errorMessage: '',
              }}
              disabled={!isUpdatable}
            />
          </Stack.Item>
          <Stack.Item grow={1}>
            <FormHookTextField
              name="supplierAddressesBySupplierId[0].postalCode"
              label="Zip/Postal Code"
              placeholder="Zip/Postal Code"
              disabled={!isUpdatable}
            />
          </Stack.Item>
        </Stack>
        <Stack
          horizontal
          verticalAlign="baseline"
          tokens={{
            childrenGap: 20,
            padding: '0px 20px',
          }}
          className={styles.mT20}
        >
          <Stack.Item grow={1}>
            <FormHookTextField
              name="supplierAddressesBySupplierId[0].telephoneNumber"
              label="Phone"
              placeholder="Phone"
              disabled={!isUpdatable}
            />
          </Stack.Item>
          <Stack.Item grow={1}>
            <FormHookTextField
              name="supplierAddressesBySupplierId[0].emailAddress"
              label="Email"
              placeholder="Email"
              disabled={!isUpdatable}
              onTextChange={(value) => {
                if (value)
                  setValue(
                    'supplierAddressesBySupplierId.0.isSendEmailAccount',
                    true
                  );
              }}
            />
          </Stack.Item>
          <Stack.Item grow={1}>
            <FormHookTextField
              name="supplierAddressesBySupplierId[0].webAddress"
              label="Website"
              placeholder="Website"
              disabled={!isUpdatable}
            />
          </Stack.Item>
        </Stack>
        <Stack
          horizontal
          verticalAlign="baseline"
          tokens={{
            childrenGap: 20,
            padding: '0px 20px',
          }}
          className={styles.mT20}
        >
          <Stack.Item grow={1}>
            <FormHookTextField
              name="federalTaxReference"
              label="Tax Reference#"
              placeholder="Tax Reference#"
              disabled={!isUpdatable}
            />
          </Stack.Item>
          <Stack.Item grow={1}>
            <FormHookTextField
              name="vatNumber"
              label="VAT ID"
              placeholder="VAT ID"
              disabled={!isUpdatable}
            />
          </Stack.Item>
        </Stack>
        <Stack
          horizontal
          tokens={{
            childrenGap: 20,
            padding: '0px 20px',
          }}
          className={styles.mT30}
        >
          <Stack.Item className={styles.checkboxContainer}>
            <FormHookCheckBox
              name="supplierAddressesBySupplierId.0.isSendEmailAccount"
              label="Send Purchase Orders"
              disabled={disableCheckboxes}
            />
          </Stack.Item>
          <Stack.Item className={styles.checkboxContainer}>
            <FormHookCheckBox
              name="isRelatedPartySupplier"
              label="Related Party"
              disabled={disableCheckboxes}
            />
          </Stack.Item>
          <Stack.Item className={styles.checkboxContainer}>
            <FormHookCheckBox
              name="isCompetitiveBidsRequired"
              label="Competitive Bids"
              disabled={disableCompetitiveBids}
            />
          </Stack.Item>
          <Stack.Item className={styles.checkboxContainer}>
            <FormHookCheckBox
              name="isContractualPurchaseOrderRequired"
              label="Contractual"
              disabled={disableCheckboxes}
            />
          </Stack.Item>
        </Stack>
        <Stack
          tokens={{
            padding: '0px 20px',
          }}
        >
          <Stack.Item className={styles.bookingRateContainer}>
            <FormHookAmount
              label="Booking Rate"
              name="vendorServiceRate"
              decimalScale={2}
              fixedDecimalScale
              styles={{ field: { textAlign: 'end' } }}
            />
          </Stack.Item>
        </Stack>
      </Stack>
    </>
  );
};
